import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

/**
 * Mirror an existing message state into a viewport-anchored toast.
 *
 * WHY a mirror rather than replacing the call sites: these pages set their message from
 * dozens of places (OrganizationsTab alone has ~100), and the messages were rendered as a
 * banner at the TOP of a long page. If you deleted a payment method at the bottom, the
 * result appeared off-screen and looked like nothing had happened. A toast is anchored to
 * the viewport, so it is visible wherever the user is scrolled — without touching any of
 * those call sites.
 *
 * Field-level validation should stay inline next to its field; a toast is worse there,
 * because the message and the input need to be read together.
 *
 * @param message  the current message ("" / null when there is none)
 * @param variant  notistack variant: "error" | "success" | "warning" | "info"
 * @param options  { persist } — defaults to persisting errors and auto-hiding the rest
 */
const useMessageToast = (message, variant = "info", options = {}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Only fire when the message CHANGES; re-renders alone must not re-toast, or a page that
  // re-renders on every keystroke would stack duplicates.
  const lastRef = useRef(null);

  useEffect(() => {
    const text = typeof message === "string" ? message.trim() : message;
    if (!text) {
      lastRef.current = null;
      return;
    }
    if (lastRef.current === text) return;
    lastRef.current = text;
    enqueueSnackbar(text, {
      variant,
      persist: options.persist ?? variant === "error",
      preventDuplicate: true,
    });
  }, [message, variant, options.persist, enqueueSnackbar]);
};

export default useMessageToast;
